import { api, authHeader } from "../configs/axiosConfigs";
import {
  getApiOrigin,
  normalizeFilesPath,
  normalizeStoredFilePath,
} from "./resolveUploadUrl";

const toFilesPath = (path, baseUrl = "") => {
  if (!path || typeof path !== "string") return "";
  const trimmed = path.trim();
  if (!trimmed) return "";

  // Already a GetFile URL — extract path query.
  try {
    if (
      trimmed.includes("FileUploader/GetFile") &&
      (trimmed.startsWith("http") || trimmed.startsWith("/"))
    ) {
      const absolute = trimmed.startsWith("http")
        ? trimmed
        : `${getApiOrigin(baseUrl) || window.location.origin}${trimmed}`;
      const url = new URL(absolute);
      const pathParam = url.searchParams.get("path");
      if (pathParam) return normalizeFilesPath(pathParam) || pathParam;
    }
  } catch {
    /* fall through */
  }

  return (
    normalizeStoredFilePath(trimmed, baseUrl) ||
    normalizeFilesPath(trimmed) ||
    ""
  );
};

const isJsonBlob = (blob) =>
  Boolean(blob && typeof blob.type === "string" && blob.type.includes("application/json"));

/**
 * Authenticated fetch of an uploaded file as Blob (uses Authorization header).
 * Prefer this over opening GetFile URLs in a new tab (which leaves the SPA / shows API host).
 */
export async function fetchUploadedFileBlob(path, baseUrl = "") {
  const filesPath = toFilesPath(path, baseUrl);
  if (!filesPath) {
    throw new Error("مسیر فایل معتبر نیست.");
  }

  try {
    const response = await api.request({
      url: `FileUploader/GetFile?path=${encodeURIComponent(filesPath)}`,
      method: "GET",
      responseType: "blob",
      headers: authHeader(),
    });

    const blob = response?.data;
    if (!blob) {
      throw new Error("فایل دریافت نشد.");
    }

    if (isJsonBlob(blob)) {
      let message = "فایل یافت نشد یا دسترسی مجاز نیست.";
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        if (parsed?.message) message = parsed.message;
      } catch {
        /* keep default */
      }
      throw new Error(message);
    }

    return blob;
  } catch (err) {
    if (err instanceof Error && err.message && !err.response) {
      throw err;
    }
    const blob = err?.response?.data;
    if (blob instanceof Blob) {
      if (isJsonBlob(blob)) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          throw new Error(parsed?.message || "فایل یافت نشد یا دسترسی مجاز نیست.");
        } catch (inner) {
          if (inner instanceof Error && inner.message) throw inner;
        }
      }
      const status = err?.response?.status;
      if (status === 404) {
        throw new Error("فایل روی سرور یافت نشد.");
      }
      if (status === 401 || status === 403) {
        throw new Error("دسترسی به فایل مجاز نیست. دوباره وارد شوید.");
      }
    }
    throw new Error(
      err?.message || "دریافت فایل پیوست ناموفق بود."
    );
  }
}

export async function createUploadedFileObjectUrl(path, baseUrl = "") {
  const blob = await fetchUploadedFileBlob(path, baseUrl);
  return {
    blob,
    objectUrl: URL.createObjectURL(blob),
  };
}

/**
 * Open or download an uploaded file without navigating the browser to the API host.
 */
export async function openUploadedFile(
  path,
  baseUrl = "",
  { download = false, fileName = "" } = {}
) {
  const { blob, objectUrl } = await createUploadedFileObjectUrl(path, baseUrl);
  const name =
    fileName ||
    String(path || "")
      .split("?")[0]
      .split("/")
      .pop() ||
    "file";

  try {
    if (download) {
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!opened) {
        // Popup blocked — fall back to download.
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    }
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
  }

  return blob;
}
